import type { StatusBucket, Task } from '@/features/project-board/types';

/**
 * 워크플로 레인 — 4개 버킷(할 일/진행/완료/제외)보다 한 단계 자세한 컬럼.
 *
 * 문서의 Status 셀에는 훨씬 촘촘한 상태어가 쓰인다(실측: pending 92 · partial 5 · implemented 4 ·
 * implemented-validation-partial 2 · code-done 2 · pending-review 2 · worktree-done 1 · reworked-v 1 …).
 * 버킷만 보면 "구현은 끝났고 검증 대기"인 것과 "손도 안 댄 것"이 같은 칸에 섞인다.
 * 그래서 보드에서 상태어 기준 레인을 선택할 수 있게 한다 — 어느 쪽도 데이터를 바꾸지 않는다.
 */
export interface WorkflowLane {
  key: string;
  label: string;
  /** 이 레인에 들어가는 상태어. 앞에서부터 먼저 매칭된다. */
  tokens: readonly string[];
  /** 상태어가 목록에 없을 때 버킷으로 흡수할 레인. */
  fallbackFor?: StatusBucket;
}

export const WORKFLOW_LANES: readonly WorkflowLane[] = [
  { key: 'pending', label: '대기', tokens: ['pending', 'gated-pending', 'planned', 'todo'], fallbackFor: 'todo' },
  { key: 'blocked', label: '차단', tokens: ['blocked'] },
  {
    key: 'implemented',
    label: '구현됨',
    tokens: ['implemented', 'code-done', 'partial', 'implemented-validation-partial', 'worktree-done', 'in-progress', 'wip'],
    fallbackFor: 'progress',
  },
  { key: 'review', label: '검증 대기', tokens: ['pending-review', 'reopened-pending'] },
  { key: 'rework', label: '재작업', tokens: ['needs-rework', 'reworked', 'reworked-v'] },
  {
    key: 'done',
    label: '완료',
    tokens: ['done', 'completed', 'verified', 'merged', 'resolved', 'integrated', 'integrated-in-phase', 'fixed'],
    fallbackFor: 'done',
  },
  {
    key: 'dropped',
    label: '제외',
    tokens: ['obsolete', 'obsolete-confirmed', 'deprecated', 'absorbed-into', 'moved-to', 'deferred', 'not-merged', 'cancelled'],
    fallbackFor: 'dropped',
  },
];

export class Workflow {
  /** task가 속한 레인. 상태어 우선, 없으면 버킷 fallback, 그것도 없으면 미분류. */
  static laneOf(task: Task): string {
    const token = task.statusToken.toLowerCase();
    const byToken = WORKFLOW_LANES.find((lane) => lane.tokens.includes(token));
    if (byToken) return byToken.key;
    const byBucket = WORKFLOW_LANES.find((lane) => lane.fallbackFor === task.status);
    return byBucket?.key ?? 'unknown';
  }
}
